import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";

const EFFECTIVE_DATE = "February 12, 2026";

export default function Privacy() {
    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-28 pb-20 px-6">
                <article className="max-w-3xl mx-auto glass p-8 sm:p-12 fade-in">
                    <p className="label-tracking text-gold">Legal</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-2 mb-2">Privacy Policy</h1>
                    <p className="text-muted text-sm">Effective {EFFECTIVE_DATE}</p>

                    <div className="mt-10 space-y-8 text-sm sm:text-base leading-relaxed">
                        <p className="text-muted">
                            SoulStar Dating ("SoulStar", "we", "us") respects your privacy. This Privacy Policy
                            explains what personal information we collect, why we collect it, how we use and share
                            it, and the rights you have. This policy applies to our website and app.
                        </p>

                        <Section title="1. Information We Collect">
                            <ul className="list-disc pl-5 space-y-2 text-muted">
                                <li><strong className="text-ink">Account data:</strong> name, email, hashed password,
                                or (if you use Google Sign-In) your Google profile name, email, and avatar.</li>
                                <li><strong className="text-ink">Astrological data:</strong> birth date, exact birth
                                time, and birth location (address and geo-coordinates). We use these to compute your
                                natal chart, Sun/Moon/Rising, and daily horoscope.</li>
                                <li><strong className="text-ink">Profile content:</strong> photos (up to 5), bio,
                                gender, orientation, messages you send.</li>
                                <li><strong className="text-ink">Selfie & biometric data (optional):</strong> if you
                                use selfie verification, a temporary photo compared to your profile photo via a Vision
                                AI model. See §5.</li>
                                <li><strong className="text-ink">Device & usage:</strong> IP address, browser type,
                                pages viewed, session duration, and crash logs.</li>
                                <li><strong className="text-ink">Payments:</strong> we do <em>not</em> store your card
                                number. Stripe processes payments and returns a customer ID + transaction status.</li>
                                <li><strong className="text-ink">Anti-abuse:</strong> hCaptcha may collect device
                                signals to distinguish humans from bots.</li>
                            </ul>
                        </Section>

                        <Section title="2. How We Use Your Information">
                            <p>We use your data to: (a) create and secure your account; (b) compute astrological
                            content; (c) surface potential matches; (d) generate AI horoscopes and synastry reports;
                            (e) process payments; (f) detect fraud and abuse; (g) send transactional emails (match
                            notifications, subscription receipts); (h) comply with legal obligations.</p>
                        </Section>

                        <Section title="3. Legal Bases (EEA / UK residents)">
                            <p>We rely on: <em>performance of contract</em> for account and matching functions;
                            <em>consent</em> for optional features like selfie verification and marketing emails;
                            <em>legitimate interests</em> for safety, fraud prevention, and analytics; and
                            <em>legal obligation</em> for tax, financial, or law-enforcement records.</p>
                        </Section>

                        <Section title="4. How We Share Information">
                            <ul className="list-disc pl-5 space-y-2 text-muted">
                                <li><strong className="text-ink">With other users:</strong> your profile (name, age,
                                photos, bio, Sun/Moon/Rising, verified badge) is visible to potential matches. Your
                                exact birth time, date, and coordinates are <em>never</em> shown to other users.</li>
                                <li><strong className="text-ink">Service providers:</strong> Stripe (payments), Google
                                (AI horoscope + selfie verification via Gemini), Emergent (hosting + AI key), hCaptcha
                                (anti-bot). Each is contractually bound to protect your data.</li>
                                <li><strong className="text-ink">Legal:</strong> when required by valid legal process,
                                to protect our rights, or to investigate abuse.</li>
                                <li><strong className="text-ink">Business transfers:</strong> in a merger, acquisition,
                                or asset sale, with notice to you.</li>
                                <li>We <strong>do not sell</strong> personal information as defined by CCPA / CPRA.</li>
                            </ul>
                        </Section>

                        <Section title="5. Biometric / Selfie Data">
                            <p>If you opt into selfie verification, we send your live selfie and your profile photo
                            to Google Gemini Vision to compute a match confidence score. The selfie and its embedding
                            are stored only as long as needed to (a) complete verification and (b) resolve any dispute
                            (max 30 days), then deleted. You can request immediate deletion at
                            <em> privacy@soulstar.love</em>. If you reside in Illinois, Texas, Washington, or any state
                            with biometric-privacy legislation, your use of this feature constitutes written consent
                            to this collection and destruction schedule.</p>
                        </Section>

                        <Section title="6. Cookies & Similar Tech">
                            <p>We use strictly necessary cookies for authentication (session token) and preferences
                            (verified-only filter, dismissed digest banners). We do not use advertising cookies. hCaptcha
                            may set anti-bot cookies. You can clear cookies at any time in your browser settings.</p>
                        </Section>

                        <Section title="7. Data Retention">
                            <p>We retain your data while your account is active and for up to 90 days after account
                            deletion (for fraud/abuse investigations and to complete outstanding subscription cycles).
                            After that, personal data is deleted or irreversibly anonymized, except records we must
                            keep for tax or legal purposes.</p>
                        </Section>

                        <Section title="8. Your Rights">
                            <p>Depending on where you live you may have the right to: (a) <strong>access</strong> your
                            data, (b) <strong>correct</strong> inaccurate data, (c) <strong>delete</strong> your data,
                            (d) <strong>port</strong> your data, (e) <strong>opt out</strong> of certain processing,
                            (f) <strong>withdraw consent</strong>. To exercise any of these, email
                            <em> privacy@soulstar.love</em>. We will respond within 30 days.</p>
                            <p className="mt-3">California residents: you have the additional rights described in the
                            CCPA/CPRA. We do not "sell" or "share" (as those terms are defined) personal data.</p>
                        </Section>

                        <Section title="9. Security">
                            <p>Passwords are stored using bcrypt hashing. Data in transit is encrypted via HTTPS/TLS.
                            Object storage (photos) is served over HTTPS with time-limited URLs. Despite our efforts,
                            no system is 100% secure — please choose a strong, unique password.</p>
                        </Section>

                        <Section title="10. International Transfers">
                            <p>SoulStar is operated from the United States. If you access the Service from outside
                            the U.S., your data will be transferred to and processed in the U.S. Where required (e.g.,
                            EEA/UK), we rely on Standard Contractual Clauses.</p>
                        </Section>

                        <Section title="11. Children">
                            <p>SoulStar is strictly for adults 18+. We do not knowingly collect data from children
                            under 18. If you believe a child has provided data, please contact us and we will delete
                            it immediately.</p>
                        </Section>

                        <Section title="12. Changes to This Policy">
                            <p>If we make material changes we will notify you by email or in-app at least 7 days
                            before the changes take effect. Continued use after the effective date constitutes acceptance.</p>
                        </Section>

                        <Section title="13. Contact Us">
                            <p>Privacy: <em>privacy@soulstar.love</em>. General: <em>support@soulstar.love</em>.</p>
                        </Section>

                        <p className="text-xs text-muted mt-10 border-t border-white/10 pt-6">
                            See also our <Link to="/terms" className="text-gold hover:underline">Terms of Service</Link>.
                            <br />
                            <em>This document is a template. Please have it reviewed by counsel licensed in your
                            jurisdiction before public launch.</em>
                        </p>
                    </div>
                </article>
            </main>
        </>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <h2 className="font-serif text-2xl text-ink mb-3">{title}</h2>
            <div className="text-muted">{children}</div>
        </div>
    );
}
